// rules/rules-percentage.ts
import { BaseRule } from '../rule-base';
import { AdjustedLine, ModifierContext } from '../modifier-models';

/** Adds a percentage of the *target* line's units (e.g. +25 %, +100 %). */
export class AddPercentageRule extends BaseRule {
    override name: string;
    constructor(
        public readonly code: string,
        private readonly pct: number           // 0.25  = +25 %
    ) {
        super();
        this.name = `${code} add ${pct * 100}%`;
    }

    run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
        const factor = 1 + this.pct;

        // Handle modifier attached in-line
        if (!line.isModifier && !line.isConsult && line.modifiers?.includes(this.code as any)) {
            if (autoApply) {
                line.adjustedUnits *= factor;
                line.trace.push(`${this.code} ×${factor}`);
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: this.code,
                    message: `Add ${(this.pct * 100).toFixed(0)}% to base units`,
                    kind: 'overwrite',
                    unitsDelta: line.adjustedUnits * factor
                });
            }
            return;
        }

        // Handle modifier as its own line
        if (line.isModifier && line.code === this.code) {
            if (autoApply) {
                line.adjustedUnits = 0;  // never bills alone
                line.trace.push('absorbed');
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: this.code,
                    message: 'Modifier will be absorbed into target line',
                    kind: 'overwrite',
                    unitsDelta: 0
                });
            }
        }
    }

    apply(lines: AdjustedLine[], ctx: ModifierContext): AdjustedLine[] {
        const factor = 1 + this.pct;

        // ① modifier attached in-line
        lines
            .filter(l => !l.isModifier && !l.isConsult && l.modifiers?.includes(this.code as any))
            .forEach(l => {
                l.adjustedUnits *= factor;
                l.trace.push(`${this.code} ×${factor}`);
            });

        // ② modifier supplied as its own line (isModifier)
        lines
            .filter(l => l.isModifier && l.code === this.code)
            .forEach(mod => {
                const idx = lines.indexOf(mod);
                const target = lines.slice(0, idx).reverse().find(x => !x.isModifier);
                if (target) {
                    target.adjustedUnits *= factor;
                    target.trace.push(`${this.code} ×${factor}`);
                    mod.adjustedUnits = 0;             // never bills alone
                    mod.trace.push('absorbed');
                }
            });

        return lines;
    }
}
